// Browser printing (spec/13 §11; F-OPS-08, F-FNB-05): the console lays a print document out as a print-ready
// 58 or 80 mm page and prints it. The server renders every job — a kitchen ticket, a receipt, a test page — into
// one print document (`packages/contracts/src/realtime/printing.ts`), and the desk device and the cloud printers
// print the same document their own way, so the three transports print the same ticket.
//
// The page is black on white roll paper, so it names its own ink rather than the console's tokens. Every piece
// of text is escaped: a product name or a note is operator- or player-typed. Money is formatted by the venue's
// formatter (integer minor units + ISO code in, "₹150.00" out) — never arithmetic here.

import type { PrintDocument, PrintLine } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { useCallback } from 'react';

/** What the layout needs from the console: the venue's money formatter and two words. */
export interface PrintWords {
  /** Integer minor units + ISO code → the amount in the venue's words ("₹1,250.00"). */
  readonly money: (money: { readonly amount: number; readonly currency: string }) => string;
  /** The print window's title. */
  readonly title: string;
  /** Above a QR line's link, which the browser prints as text ("Scan for the invoice"). */
  readonly scanLabel: string;
}

export const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] ?? char);

/** The printable width of each roll, in millimetres: the paper less the printer's own margins. */
const PRINTABLE_MM: Readonly<Record<58 | 80, number>> = { 58: 48, 80: 72 };

/** A document's roll: 58 or 80 mm; anything else is laid out for 80 mm, the common counter roll. */
export function paperWidthOf(document: Pick<PrintDocument, 'paperWidth'>): 58 | 80 {
  return document.paperWidth === 58 ? 58 : 80;
}

function lineClasses(line: PrintLine, base: string): string {
  const classes = [base];
  if (line.align === 'center' || line.align === 'right') classes.push(`a-${line.align}`);
  if (line.bold === true) classes.push('b');
  if (line.size === 'tall' || line.size === 'large') classes.push(`s-${line.size}`);
  return classes.join(' ');
}

function renderLine(line: PrintLine, words: PrintWords): string {
  const text = escapeHtml(line.text ?? '');
  switch (line.kind) {
    case 'text':
      return `<p class="${lineClasses(line, 'l')}">${text === '' ? '&nbsp;' : text}</p>`;
    case 'amount': {
      const amount = line.amount === undefined ? '' : escapeHtml(words.money(line.amount));
      return `<p class="${lineClasses(line, 'row')}"><span>${text}</span><span class="m">${amount}</span></p>`;
    }
    case 'item': {
      const quantity = line.quantity ?? 1;
      const amount = line.amount === undefined ? '' : `<span class="m">${escapeHtml(words.money(line.amount))}</span>`;
      const notes = (line.notes ?? []).map((note) => `<li>${escapeHtml(note)}</li>`).join('');
      return (
        `<div class="item"><p class="${lineClasses(line, 'row')}"><span>${quantity} × ${text}</span>${amount}</p>` +
        (notes === '' ? '' : `<ul class="notes">${notes}</ul>`) +
        '</div>'
      );
    }
    case 'rule':
      return '<hr class="rule">';
    case 'feed': {
      const lines = Math.min(10, Math.max(1, Math.trunc(line.lines ?? 1)));
      return `<div class="feed" style="height:${(lines * 1.3).toFixed(1)}em"></div>`;
    }
    case 'qr':
      // No QR component in the kit yet: the link, small, under its label — a phone can still type it.
      return `<div class="qr"><p class="qr-label">${escapeHtml(words.scanLabel)}</p><p class="qr-text">${text}</p></div>`;
    default:
      return '';
  }
}

/** The whole print-ready page for a document, as HTML — pure, so it is tested without a window. */
export function printDocumentHtml(document: PrintDocument, words: PrintWords): string {
  const width = paperWidthOf(document);
  const printable = PRINTABLE_MM[width];
  const margin = (width - printable) / 2;
  const body = document.lines.map((line) => renderLine(line, words)).join('');
  const base = width === 58 ? 11 : 12;
  const style =
    `@page{size:${width}mm auto;margin:0}` +
    `html,body{margin:0;padding:0;background:#fff;color:#000}` +
    `body{width:${printable}mm;padding:3mm ${margin}mm;font-family:ui-monospace,"SFMono-Regular",Menlo,Consolas,monospace;font-size:${base}px;line-height:1.3}` +
    'p{margin:0}.l{white-space:pre-wrap;word-break:break-word}.a-center{text-align:center}.a-right{text-align:right}.b{font-weight:700}' +
    '.s-tall{font-size:1.6em;line-height:1.2}.s-large{font-size:2.2em;line-height:1.15;font-weight:700;letter-spacing:.04em}' +
    '.row{display:flex;justify-content:space-between;gap:2mm}.row>span:first-child{word-break:break-word}.m{white-space:nowrap;font-variant-numeric:tabular-nums}' +
    '.row.a-center{justify-content:center}.item{margin:.6mm 0}.notes{list-style:none;margin:0;padding:0 0 0 4mm;font-size:.9em}.notes li::before{content:"- "}' +
    '.rule{border:0;border-top:1px dashed #000;margin:1.5mm 0}.qr{text-align:center;margin:2mm 0}.qr-label{font-weight:700}.qr-text{font-size:.8em;word-break:break-all}';
  return (
    `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>${escapeHtml(words.title)}</title>` +
    `<style>${style}</style></head><body data-paper="${width}">${body}</body></html>`
  );
}

/**
 * Open the page in a print window and print it. Returns `false` when a popup blocker stopped the window, so the
 * caller can say so; the console stays where it was either way.
 */
export function openPrintDocument(document: PrintDocument, words: PrintWords, host: Pick<Window, 'open'> = window): boolean {
  const width = paperWidthOf(document);
  const win = host.open('', '_blank', `width=${width === 58 ? 300 : 380},height=640`);
  if (win === null) return false;
  win.document.open();
  win.document.write(printDocumentHtml(document, words));
  win.document.close();
  win.focus();
  // Printing after the page has laid out; a browser that has already loaded it prints straight away.
  if (win.document.readyState === 'complete') win.print();
  else win.addEventListener('load', () => win.print(), { once: true });
  return true;
}

/** A function that prints a document in the browser with the venue's money and the console's words. */
export function usePrintDocument(): (document: PrintDocument, title: string) => boolean {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  return useCallback(
    (document: PrintDocument, title: string) => openPrintDocument(document, { money: (money) => format.money(money), title, scanLabel: t('printing.scanForInvoice') }),
    [format, t],
  );
}
